import { useCallback, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import type { SubmissionRow } from '../../types/submission';
import type { RestoreSubmissionBody } from '../../types/changelog';
import { useRestoreSubmission } from '../../hooks/useRestoreSubmission';

export interface RestoreTarget {
  change_log_id: number | 'original';
  to_original: boolean;
  /** Human-readable phrase for the confirmation copy. */
  label: string;
}

interface RestoreDialogProps {
  resource: string;
  row: SubmissionRow;
  target: RestoreTarget;
  onClose: () => void;
  onRestored: () => void;
}

/**
 * Confirmation dialog for a restore. Requires the reviewer to enter a
 * `change_reason` (server-mandated). Sends `restore_to_original: true`
 * alongside `change_log_id: 'original'` when the user picked the
 * pre-edit target — belt-and-braces per the spec's two-way accept
 * ("`change_log_id: 'original'` **or** `restore_to_original: true`").
 */
export function RestoreDialog({ resource, row, target, onClose, onRestored }: RestoreDialogProps) {
  const [changeReason, setChangeReason] = useState('');
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const { restore, restoring, error } = useRestoreSubmission(resource, row.id);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setAttemptedSubmit(true);
      if (changeReason.trim().length === 0) return;
      const body: RestoreSubmissionBody = {
        change_reason: changeReason.trim(),
        previous_updated_at: row.updated_at ?? null,
        change_log_id: target.change_log_id,
        ...(target.to_original ? { restore_to_original: true } : {}),
      };
      try {
        await restore(body);
        onRestored();
      } catch {
        // useRestoreSubmission already exposes the error via `error`.
      }
    },
    [changeReason, restore, row.updated_at, target, onRestored],
  );

  const reasonInvalid = attemptedSubmit && changeReason.trim().length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-rgf-gray-200 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-rgf-gray-900">Confirm restore</h3>
            <p className="mt-0.5 text-xs text-rgf-gray-500">
              You are about to restore this record to {target.label}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {error && (
          <div className="mx-5 mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-700" />
            <div className="text-red-900">{error.message}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <label className="block text-xs font-medium text-rgf-gray-700">
            Change reason <span className="text-red-500">*</span>
            <input
              type="text"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Why are you restoring?"
              className="mt-1.5 w-full rounded-lg border border-rgf-gray-300 px-3 py-2 text-sm text-rgf-gray-900 focus:border-rgf-primary-500 focus:outline-none focus:ring-1 focus:ring-rgf-primary-500"
              disabled={restoring}
            />
          </label>
          {reasonInvalid && (
            <p className="text-xs text-red-600">A change reason is required.</p>
          )}
          <footer className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-rgf-gray-200 px-4 py-2 text-sm font-medium text-rgf-gray-700 hover:border-rgf-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={restoring || changeReason.trim().length === 0}
              className="rounded-lg bg-rgf-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-rgf-primary-700 disabled:cursor-not-allowed disabled:bg-rgf-gray-300"
            >
              {restoring ? 'Restoring…' : 'Restore'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
